import React, { useContext, useEffect, useState, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  ListItemText,
  Button,
  CircularProgress,
  Checkbox,
  Typography,
  IconButton,
  Box,
  Divider,
  ListItemAvatar,
  Avatar,
  DialogActions,
  useTheme,
  Paper,
  Tooltip,
  Zoom,
  Backdrop,
  alpha,
} from "@mui/material";
import { VideoCallContext } from "store/VideoCallContext";
import RefreshIcon from "@mui/icons-material/Refresh";
import CloseIcon from "@mui/icons-material/Close";
import CallIcon from "@mui/icons-material/Call";
import VideocamIcon from "@mui/icons-material/Videocam";
import { styled, keyframes } from "@mui/material/styles";
import { NewSearchButton } from "components/Buttons/Buttons";
import phone_icon from "../../assets/img/phone_icon_white.svg";
import reload_icon from "../../assets/img/reload_icon_blue.svg";
import { NewCreateOfferButton } from "components/Buttons/Buttons";
import colors from "colors/color";
import { useTranslation } from "react-i18next";
// Define animations
const pulseLight = keyframes`
  0% {
    transform: translateX(-100%) translateY(-100%) rotate(45deg);
    opacity: 0;
  }
  50% {
    opacity: 0.5;
  }
  100% {
    transform: translateX(100%) translateY(100%) rotate(45deg);
    opacity: 0;
  }
`;

const pulse = keyframes`
  0% { transform: scale(1); opacity: 0.8; }
  50% { transform: scale(1.1); opacity: 1; }
  100% { transform: scale(1); opacity: 0.8; }
`;

const shadowPulse = keyframes`
  0% { box-shadow: 0 0 8px 2px ${alpha("#0017E7", 0.3)}; }
  50% { box-shadow: 0 0 16px 4px ${alpha("#E700C8", 0.5)}; }
  100% { box-shadow: 0 0 8px 2px ${alpha("#0017E7", 0.3)}; }
`;

// Styled components
const PulsingContainer = styled(Box)({
  position: "absolute",
  overflow: "hidden",
  width: "100%",
  height: "100%",
  top: 0,
  left: 0,
  pointerEvents: "none",
  zIndex: 0,
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background:
      "linear-gradient(45deg, rgb(255, 255, 255) 0%,  rgba(255, 204, 248, 0.5) 100%)",
    animation: `${pulseLight} 2.5s infinite`,
    width: "200%",
    height: "200%",
    zIndex: 0,
    pointerEvents: "none",
  },
});

const StyledButton = styled(Button)(({ theme, color }) => ({
  borderRadius: "50px",
  padding: "10px 24px",
  fontWeight: 500,
  textTransform: "none",
  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
  transition: "all 0.3s ease",
  "&:hover": {
    transform: "translateY(-3px)",
    boxShadow: "0 6px 16px rgba(0, 0, 0, 0.2)",
  },
}));
const CompanyLogo = styled(Box)(({ theme, logo }) => ({
  overflow: "hidden",
  minWidth: "48px",
  minHeight: "48px",
  maxWidth: "48px",
  maxHeight: "48px",
  background: logo 
    ? `url(${logo}) center/cover no-repeat` 
    : "linear-gradient(135deg, rgba(0, 23, 231, 0.5) -54.69%, rgba(255, 204, 248, 0.5) 66.41%)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: 14,
  color: "#fff",
  fontSize: "0.75rem",
  textAlign: "center",
}));
const StyledUserAvatar = styled(Avatar)(({ theme, selected }) => ({
  width: 50,
  height: 50,
  border: selected
    ? `2px solid ${theme.palette.primary.main}`
    : "2px solid transparent",
  animation: selected ? `${shadowPulse} 4s ease-in-out infinite` : "none",
  fontSize: "18px",
  backgroundColor: selected
    ? alpha(theme.palette.primary.main, 0.1)
    : alpha(theme.palette.grey[500], 0.1),
  position: "relative",
  zIndex: 2,
}));

const ConversationDialog = ({
  setChoosenContacts,
  open,
  onClose,
  conversations = [],
  isLoading,
  isError,
  refetch,
  calling,
  receiverId,
}) => {
  const { t } = useTranslation();
  const { setVideoCallLaunched } = useContext(VideoCallContext);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const theme = useTheme();
  const listRef = useRef(null);

  // Initialize selectedUsers with receiverId when dialog opens
  useEffect(() => {
    if (open && receiverId) {
      setSelectedUsers([receiverId]);
    }
  }, [open, receiverId]);

  useEffect(() => {
    console.log('conversations--:',conversations)
  }, [conversations]);
  // Reset selected users when dialog closes
  useEffect(() => {
    if (!open) {
      setSelectedUsers([]);
    }
  }, [open]);

  const handleToggleUser = (userId) => {
    if (userId === receiverId) return; // Prevent unchecking receiverId
    
    // Save current scroll position
    const scrollPosition = listRef.current?.scrollTop;
    
    setSelectedUsers((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
    
    // Restore scroll position after state update
    if (scrollPosition !== undefined) {
      setTimeout(() => {
        if (listRef.current) {
          listRef.current.scrollTop = scrollPosition;
        }
      }, 0);
    }
  };

  const handleStartCall = () => {
    setChoosenContacts(selectedUsers);
    setVideoCallLaunched(true);
    onClose(true);
    calling();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      BackdropComponent={(props) => (
        <Backdrop
          {...props}
          sx={{
            backdropFilter: "blur(5px)",
            backgroundColor: "rgba(0, 0, 0, 0.2)",
            transition: "backdrop-filter 0.3s, background-color 0.3s",
          }}
        />
      )}
      PaperComponent={(props) => (
        <Paper
          {...props}
          sx={{
            backgroundColor: "rgba(255, 255, 255, 0.7)",
            backdropFilter: "blur(30px)",
            borderRadius: 6,
            border: "1px solid rgba(255, 255, 255, 0.4)",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.4)",
            overflow: "hidden",
            position: "relative",
          }}
        />
      )}
      PaperProps={{
        elevation: 24,
      }}
    >
      <PulsingContainer />
      <DialogContent
        sx={{
          textAlign: "center",
          py: 4,
          px: 3,
          position: "relative",
          zIndex: 1,
          background: "transparent",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 2,
            }}
          >
            <Avatar
              sx={{
                width: 60,
                height: 60,
                bgcolor: theme.palette.primary.main,
                animation: `${pulse} 2s ease-in-out infinite`,
                boxShadow: `0 0 12px ${alpha(theme.palette.primary.main, 0.5)}`,
                mb: 1,
              }}
            >
              <VideocamIcon sx={{ fontSize: 32 }} />
            </Avatar>
          </Box>

          <Typography
            sx={{
              fontFamily: "Poppins",
              fontWeight: "600",
              fontSize: "24px",
              color: "#000000",
              textAlign: "center",
              marginBottom: "10px",
            }}
          >
            {t('conversationDialog.title')}
          </Typography>

          <Typography
            sx={{
              fontFamily: "Poppins",
              fontWeight: "400",
              fontSize: "16px",
              color: "#000000",
              textAlign: "center",
              marginBottom: "20px",
              maxWidth: "80%",
              mx: "auto",
            }}
          >
            {t('conversationDialog.subtitle')}
          </Typography>
        </Box>

        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress size={40} />
          </Box>
        ) : isError ? (
          <Paper
            elevation={0}
            sx={{
              p: 3,
              textAlign: "center",
              bgcolor: alpha(theme.palette.error.main, 0.1),
              color: theme.palette.error.main,
              borderRadius: 2,
              mb: 3,
            }}
          >
            <Typography variant="body1">
              {t('conversationDialog.errorLoading')}
            </Typography>
          </Paper>
        ) : (
          <List
            ref={listRef}
            sx={{
              pt: 1,
              maxHeight: "300px",
              overflowY: "auto",
              "&::-webkit-scrollbar": {
                width: "8px",
              },
              "&::-webkit-scrollbar-track": {
                background: alpha(theme.palette.background.default, 0.5),
                borderRadius: "10px",
              },
              "&::-webkit-scrollbar-thumb": {
                background: alpha(theme.palette.primary.main, 0.3),
                borderRadius: "10px",
                "&:hover": {
                  background: alpha(theme.palette.primary.main, 0.5),
                },
              },
            }}
          >
            {conversations && conversations.length > 0 ? (
              conversations.map((user) => (
                <ListItem
                  key={user.id}
                  sx={{
                    borderRadius: 4,
                    mb: 1.5,
                    p: 1.5,
                    transition: "all 0.3s ease",
                    backgroundColor: selectedUsers.includes(user.id)
                      ? alpha(theme.palette.primary.main, 0.1)
                      : alpha(theme.palette.background.paper, 0.5),
                    "&:hover": {
                      backgroundColor: selectedUsers.includes(user.id)
                        ? alpha(theme.palette.primary.main, 0.15)
                        : alpha(theme.palette.background.paper, 0.7),
                      transform: "translateY(-2px)",
                      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
                    },
                    border: selectedUsers.includes(user.id)
                      ? `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                      : "1px solid rgba(255, 255, 255, 0.15)",
                    cursor: "pointer",
                  }}
                  onClick={() => handleToggleUser(user.id)}
                  secondaryAction={
                    <Checkbox
                      edge="end"
                      // onChange={() => handleToggleUser(user.id)}
                      checked={selectedUsers.includes(user.id)}
                      disabled={user.id === receiverId}
                      sx={{
                        "&.Mui-checked": {
                          color: theme.palette.primary.main,
                        },
                      }}
                    />
                  }
                >
                  <ListItemAvatar>
                    <CompanyLogo
                      selected={selectedUsers.includes(user.id)}
                      logo={user.image}
                      sx={{
                        bgcolor: user.image ? 'transparent' :
                          user.id === receiverId
                            ? theme.palette.primary.main
                            : theme.palette.grey[500],
                        color: "#fff", 
                        fontWeight: 500,
                      }}
                    >
                      {!user.image && `${user.first_name[0]}${user.last_name[0]}`}
                    </CompanyLogo>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 500,
                            fontFamily: "Poppins",
                            color: "#000",
                          }}
                        >
                          {`${user.first_name} ${user.last_name}`}
                        </Typography>
                        {user.id === receiverId && (
                          <Typography
                            component="span"
                            sx={{
                              fontSize: "0.75rem",
                              color: theme.palette.primary.main,
                              bgcolor: alpha(theme.palette.primary.main, 0.1),
                              px: 1,
                              py: 0.25,
                              borderRadius: "8px",
                              fontFamily: "Poppins",
                            }}
                          >
                            {t('conversationDialog.required')}
                          </Typography>
                        )}
                      </Box>
                    }
                    secondary={
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          fontFamily: "Poppins",
                          fontSize: "0.85rem",
                        }}
                      >
                        {user.email}
                      </Typography>
                    }
                  />
                </ListItem>
              ))
            ) : (
              <Box
                sx={{
                  p: 4,
                  textAlign: "center",
                  bgcolor: alpha(theme.palette.warning.main, 0.05),
                  borderRadius: 2,
                }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    fontFamily: "Poppins",
                    color: theme.palette.text.secondary,
                  }}
                >
                  {t('conversationDialog.noConversations')}
                </Typography>
              </Box>
            )}
          </List>
        )}

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            mt: 4,
            gap: 2,
          }}
        >
          <NewCreateOfferButton
            onClick={refetch}
            variant='outlined'
            sx={{
              borderColor: colors.primary,
              backgroundColor:'#fff',
              textTransform: "none",
              gap:1,
              paddingX:'12px',
            }}
          >
            <img src={reload_icon} alt="reload_icon" />
            <Typography
              sx={{
                textTransform:'none',
                fontSize:'14px',
                fontWeight:'400',
                fontFamily:'Poppins',
                color:colors.primary,
              }}
            >
              {t('conversationDialog.refresh')}
            </Typography>
          </NewCreateOfferButton>

          <NewSearchButton
            onClick={handleStartCall}
            variant="contained"
            color="primary"
            disabled={selectedUsers.length === 0}
            sx={{
              textTransform: "none",
              gap:1,
              paddingX:'12px',
            }}
          >
            <img src={phone_icon} alt="phone_icon" />
            <Typography
              sx={{
                textTransform:'none',
                fontSize:'14px',
                fontWeight:'400',
                fontFamily:'Poppins',
                color:'#fff',
              }}
            >
              {t('conversationDialog.startCall')}
            </Typography>
          </NewSearchButton>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ConversationDialog;
